import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CountryCode } from "libphonenumber-js";
import { RootState } from "../store";
import { CategoryLookupModel } from "./categorySlice";

export interface CountryType {
  code: CountryCode;
  label: string;
  phone: string;
  suggested?: boolean;
}
export interface PhoneNumberModel {
  prefix?: CountryType;
  number?: string;
}

export interface PlaceModel {
  id: string;
  value: string;
}

export interface EstimateModel {
  name: string;
  email: string;
  phoneNumber: PhoneNumberModel;
  businessName: string;
  businessCategory: CategoryLookupModel | null;
  town?: PlaceModel;
  tier?: number;
}

export interface EstimationResultModel {
  // id?: string;
  level: 1 | 2 | 3 | 4;
  tier: 300 | 600 | 1000 | 2500;
  adViews: EstimateValues;
  adClicks: EstimateValues;
}

export interface EstimateValues {
  lowerValue: number;
  higherValue: number;
}

export interface EstimateState {
  data: EstimateModel;
  result?: EstimationResultModel[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: EstimateState = {
  data: {
    name: "",
    email: "",
    businessCategory: null,
    phoneNumber: {
      prefix: {
        code: "AU",
        label: "Australia",
        phone: "61",
      },
      number: undefined,
    },
    businessName: "",
    tier: 600,
  },
  result: [
    {
      level: 1,
      tier: 300,
      adViews: {
        lowerValue: 2646,
        higherValue: 5125,
      },
      adClicks: {
        lowerValue: 78,
        higherValue: 212,
      },
    },
    {
      level: 2,
      tier: 600,
      adViews: {
        lowerValue: 5970,
        higherValue: 7290,
      },
      adClicks: {
        lowerValue: 199,
        higherValue: 369,
      },
    },
    {
      level: 3,
      tier: 1000,
      adViews: {
        lowerValue: 8352,
        higherValue: 10384,
      },
      adClicks: {
        lowerValue: 335,
        higherValue: 526,
      },
    },
    {
      level: 4,
      tier: 2500,
      adViews: {
        lowerValue: 9156,
        higherValue: 13452,
      },
      adClicks: {
        lowerValue: 553,
        higherValue: 751,
      },
    },
  ],
  status: "idle",
  error: null,
};

const estimateSlice = createSlice({
  name: "estimate",
  initialState,
  reducers: {
    changeName(state, action: PayloadAction<string>) {
      state.data.name = action.payload;
    },
    changeEmail(state, action: PayloadAction<string>) {
      state.data.email = action.payload;
    },
    changePhoneNumber(state, action: PayloadAction<PhoneNumberModel>) {
      state.data.phoneNumber = action.payload;
    },
    changeBusinessName(state, action: PayloadAction<string>) {
      state.data.businessName = action.payload;
    },
    changeBusinessCategory(
      state,
      action: PayloadAction<CategoryLookupModel | null>
    ) {
      state.data.businessCategory = action.payload;
    },
    changeTown(state, action: PayloadAction<PlaceModel | undefined>) {
      state.data.town = action.payload;
    },
    changeTier(state, action: PayloadAction<number | undefined>) {
      state.data.tier = action.payload;
    },
    setStatusLoading(state, action: PayloadAction<void>) {
      state.status = "loading";
    },
    setStatusSucceeded(state, action: PayloadAction<void>) {
      state.status = "succeeded";
    },
    setStatusFailed(state, action: PayloadAction<void>) {
      state.status = "failed";
    },
    setEstimateResult(
      state,
      action: PayloadAction<EstimationResultModel[] | undefined>
    ) {
      state.result = action.payload;
    },
  },
});

export const {
  changeName,
  changeEmail,
  changePhoneNumber,
  changeBusinessName,
  changeBusinessCategory,
  changeTown,
  changeTier,
  setStatusLoading,
  setStatusSucceeded,
  setStatusFailed,
  setEstimateResult,
} = estimateSlice.actions;
export default estimateSlice.reducer;

export const selectEstimate = (state: RootState): EstimateModel =>
  state.estimate.data;

export const selectEstimateStatus = (state: RootState) => state.estimate.status;

export const selectEstimationResult = (
  state: RootState
): EstimationResultModel[] | undefined => state.estimate.result;
